package br.com.texeratx.boardgametimeapp.ui.screens.matches.detail

import androidx.lifecycle.ViewModel
import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import androidx.lifecycle.viewModelScope
import br.com.texeratx.boardgametimeapp.data.repository.ExpansionRepository
import br.com.texeratx.boardgametimeapp.data.repository.MatchRepository
import br.com.texeratx.boardgametimeapp.ui.screens.expansions.model.ExpansionUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.toUI
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class MatchDetailViewModel(
    private val matchRepository: MatchRepository,
    private val expansionRepository: ExpansionRepository,
    private val lang: LanguageManager
) : ViewModel() {

    private val _match = MutableStateFlow<MatchListItemUI?>(null)
    val match: StateFlow<MatchListItemUI?> = _match.asStateFlow()

    private val _state = MutableStateFlow(MatchDetailUiState())
    val state: StateFlow<MatchDetailUiState> = _state.asStateFlow()

    fun setMatch(match: MatchListItemUI) {
        _match.value = match

        if (match.isStub) {
            refreshFromApi(match.id)
            return
        }

        // já veio completo → só carrega expansões se precisar
        if (match.expansionIds.isNotEmpty()) {
            loadExpansions(match.expansionIds)
        } else {
            _state.update { it.copy(expansions = emptyList()) }
        }
    }

    fun updateMatch(match: MatchListItemUI) {
        _match.value = match

        if (match.expansionIds.isNotEmpty()) {
            loadExpansions(match.expansionIds)
        } else {
            _state.update { it.copy(expansions = emptyList()) }
        }
    }

    private fun loadExpansions(expansionIds: List<Long>) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            expansionRepository
                .getExpansionsByIds(expansionIds)
                .onSuccess { items ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            expansions = items.map {
                                ExpansionUI(
                                    id = it.id,
                                    name = it.name,
                                    imageUrl = it.imageUrl
                                )
                            }
                        )
                    }
                }
                .onFailure { e ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = e.message ?: lang.strings.value.errorLoadingExpansions
                        )
                    }
                }
        }
    }

    fun showDeleteConfirmation() {
        _state.update { it.copy(showDeleteConfirmation = true) }
    }

    fun dismissDeleteConfirmation() {
        _state.update { it.copy(showDeleteConfirmation = false) }
    }

    fun deleteMatch(onSuccess: () -> Unit) {
        val matchId = _match.value?.id ?: return
        viewModelScope.launch {
            _state.update { it.copy(isDeleting = true) }
            matchRepository.deleteMatch(matchId)
                .onSuccess { onSuccess() }
                .onFailure { e ->
                    _state.update {
                        it.copy(
                            isDeleting = false,
                            error = e.message ?: lang.strings.value.errorDeletingMatch
                        )
                    }
                }
            _state.update { it.copy(isDeleting = false) }
        }
    }

    private fun refreshFromApi(matchId: Long) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }

            matchRepository
                .getMatchDetailsById(matchId)
                .onSuccess { dto ->
                    val full = dto.toUI()

                    _match.value = full
                    _state.update { it.copy(isLoading = false, error = null) }

                    if (full.expansionIds.isNotEmpty()) {
                        loadExpansions(full.expansionIds)
                    } else {
                        _state.update { it.copy(expansions = emptyList()) }
                    }
                }
                .onFailure { e ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = e.message ?: lang.strings.value.errorLoadingMatchDetails
                        )
                    }
                }
        }
    }
}
